import { useState } from 'react';
import {
  AlertTriangle,
  Check,
  Cloud,
  CloudDownload,
  Copy,
  HardDriveDownload,
  Loader2,
  Trash2,
  Upload,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { MediaTagEditor } from '@/components/media/MediaTagEditor';
import { MediaPreview } from '@/components/media/MediaPreview';
import { useMediaLibrary } from '@/hooks/useMediaLibrary';
import { useToast } from '@/hooks/useToast';
import { formatBytes, formatDuration, isUploaded, publicUrl, type MediaItem } from '@/lib/media';
import { cn } from '@/lib/utils';

interface MediaDetailDialogProps {
  item: MediaItem | undefined;
  onOpenChange: (open: boolean) => void;
}

/**
 * Full detail view for one media item: preview, alt text, tags, upload
 * state per Blossom server, and destructive actions.
 *
 * Kept as a dialog rather than a route because the media page, the
 * editor's insert picker, and (potentially) other surfaces all need it —
 * a dialog composes into any of them without a navigation round trip
 * that would lose editor state.
 */
export function MediaDetailDialog({ item, onOpenChange }: MediaDetailDialogProps) {
  const { update, remove, evictBlob, restoreBlob, upload, tags: allTags, servers } = useMediaLibrary();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!item) return null;

  const url = publicUrl(item);
  const uploaded = isUploaded(item);
  const remaining = servers.filter((s) => !item.uploads.some((u) => u.server === s));

  const handleUpload = async () => {
    setIsUploading(true);
    try {
      const next = await upload(item.sha256);
      toast({
        title: 'Uploaded',
        description: `"${item.name}" is now on ${next.uploads.length} server${next.uploads.length === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      toast({
        title: "Couldn't upload this file",
        description: error instanceof Error ? error.message : 'Something went wrong.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'URL copied' });
    } catch {
      toast({ title: "Couldn't copy the URL", variant: 'destructive' });
    }
  };

  return (
    <Dialog open={Boolean(item)} onOpenChange={onOpenChange}>
      {/* Long unbroken filenames (like a sha256-hash one) overflow the
          grid container unless the column content is explicitly bounded
          — and `break-words` alone can't break hash-like strings. `min-w-0`
          on the dialog content plus `break-all` on the title is what
          keeps this from overflowing the whole dialog horizontally. */}
      <DialogContent className="max-h-[90vh] max-w-2xl min-w-0 overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="break-all pr-6">{item.name}</DialogTitle>
          <DialogDescription>
            {item.type} · {formatBytes(item.size)}
            {item.width && item.height ? ` · ${item.width}×${item.height}` : ''}
            {item.duration ? ` · ${formatDuration(item.duration)}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-5">
          <MediaPreview item={item} />

          {/* Alt text is nudged, not required: an accessible default is
              better than a blocked workflow, but the prompt has to be
              visible enough that writing it is the obvious next step. */}
          <div className="space-y-1.5">
            <Label htmlFor="media-alt">Alt text</Label>
            <Input
              id="media-alt"
              value={item.alt ?? ''}
              onChange={(e) => void update(item.sha256, { alt: e.target.value })}
              placeholder="Describe this file for screen readers…"
            />
            {!item.alt?.trim() && (
              <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500">
                <AlertTriangle className="size-3.5 shrink-0" />
                No alt text yet — readers using a screen reader won't know what this shows.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Tags</Label>
            <MediaTagEditor
              tags={item.tags}
              onChange={(next) => void update(item.sha256, { tags: next })}
              suggestions={allTags}
            />
          </div>

          <Separator />

          {/* Storage state, spelled out per server. "Where does this
              actually live?" is the question a local-first media library
              has to answer unambiguously. */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label>Storage</Label>
              <div className="flex flex-wrap gap-2">
                {url && (
                  <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
                    <Copy className="size-4" />
                    Copy URL
                  </Button>
                )}
                {remaining.length > 0 && (
                  <Button type="button" size="sm" onClick={handleUpload} disabled={isUploading || !item.hasBlob}>
                    {isUploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                    {uploaded ? `Upload to ${remaining.length} more` : 'Upload'}
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-1.5 rounded-md border bg-muted/20 p-3 text-sm">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'inline-flex size-4 shrink-0 items-center justify-center rounded-full',
                    item.hasBlob ? 'bg-emerald-500/15 text-emerald-600' : 'bg-muted text-muted-foreground',
                  )}
                >
                  {item.hasBlob ? <Check className="size-3" /> : null}
                </span>
                <span className={item.hasBlob ? '' : 'text-muted-foreground'}>
                  {item.hasBlob ? 'Stored on this device' : 'Not stored on this device'}
                </span>
                {/* Restore is only offered when the file is uploaded but
                    evicted locally — a genuinely local-only file has
                    nothing to restore from. */}
                {!item.hasBlob && uploaded && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="ml-auto h-7 shrink-0 gap-1.5 text-xs"
                    disabled={isRestoring}
                    onClick={async () => {
                      setIsRestoring(true);
                      try {
                        await restoreBlob(item.sha256);
                        toast({ title: 'Restored to this device' });
                      } catch (error) {
                        toast({
                          title: "Couldn't restore this file",
                          description: error instanceof Error ? error.message : 'Something went wrong.',
                          variant: 'destructive',
                        });
                      } finally {
                        setIsRestoring(false);
                      }
                    }}
                  >
                    {isRestoring ? <Loader2 className="size-3.5 animate-spin" /> : <CloudDownload className="size-3.5" />}
                    Restore
                  </Button>
                )}
              </div>

              {item.uploads.map((entry) => (
                <div key={entry.server} className="flex items-center gap-2">
                  <span className="inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600">
                    <Check className="size-3" />
                  </span>
                  <span className="min-w-0 flex-1 truncate font-mono text-xs" title={entry.server}>
                    {hostOf(entry.server)}
                  </span>
                </div>
              ))}

              {!uploaded && (
                <p className="flex items-start gap-2 pt-1 text-xs text-muted-foreground">
                  <Cloud className="mt-0.5 size-3.5 shrink-0" />
                  Local only. It'll be uploaded automatically when you publish an article that uses it.
                </p>
              )}
            </div>
          </div>

          <Separator />

          <div className="flex flex-wrap gap-2">
            {/* Only offered once the file is safely hosted — evicting a
                local-only file would destroy its only copy. */}
            {uploaded && item.hasBlob && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  await evictBlob(item.sha256);
                  toast({
                    title: 'Local copy removed',
                    description: 'The file stays available from your Blossom servers.',
                  });
                }}
              >
                <HardDriveDownload className="size-4" />
                Free up space on this device
              </Button>
            )}

            <Button
              type="button"
              variant={confirmDelete ? 'destructive' : 'outline'}
              size="sm"
              onClick={async () => {
                if (!confirmDelete) {
                  setConfirmDelete(true);
                  return;
                }
                await remove(item.sha256);
                onOpenChange(false);
                toast({
                  title: 'Removed from your library',
                  description: uploaded
                    ? 'Copies already on Blossom servers are unaffected.'
                    : undefined,
                });
              }}
            >
              <Trash2 className="size-4" />
              {confirmDelete ? 'Really remove?' : 'Remove from library'}
            </Button>
          </div>

          {uploaded && (
            <p className="text-xs text-muted-foreground">
              Removing a file here only affects this device. Blossom servers are content-addressed and have no
              guaranteed deletion, so anything already uploaded — and any article already published with it — keeps
              working.
            </p>
          )}

          {item.tags.length === 0 && (
            <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
              Untagged
            </Badge>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function hostOf(server: string): string {
  try {
    return new URL(server).host;
  } catch {
    return server;
  }
}
